import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { listSessions } from '../utils/sessionManager';

/**
 * HomeViewPremium - Redesigned home view with Apple-inspired premium aesthetics
 * Clean, refined, professional with MentorMind branding
 */
const HomeViewPremium = ({
  companyIdSlug,
  vertical,
  companyConfig,
  onStartVoiceSession,
  onStartTextSession,
  onStartWithFramework,
  onResumeSession
}) => {
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    if (companyIdSlug) {
      const sessions = listSessions(companyIdSlug, 3); // Only show 3 recent
      setRecentSessions(sessions);
    }
  }, [companyIdSlug]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffHours = diffTime / (1000 * 60 * 60);

    if (diffHours < 24) return 'Today';
    if (diffHours < 48) return 'Yesterday';
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Derive display name from companyConfig (coach name or company name)
  const displayName = companyConfig?.ui?.name
    || companyConfig?.coachName
    || companyConfig?.company_name
    || companyConfig?.name
    || '';

  return (
    <div className="mm-home-premium">
      {/* Hero Section */}
      <div className="mm-home-hero">
        <p className="mm-greeting">Welcome back</p>
        <h1 className="mm-hero-title">
          {displayName ? `${displayName}` : 'Your AI Coach'}
        </h1>
        <p className="mm-hero-subtitle">Ready to help you grow</p>
      </div>

      {/* Primary CTAs */}
      <div className="mm-ctas-section">
        {/* Voice Session - Primary */}
        <motion.button
          className="mm-cta-voice"
          onClick={onStartVoiceSession}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="mm-cta-content">
            <span className="mm-cta-label">Start Voice Session</span>
            <span className="mm-cta-description">Begin a coaching conversation</span>
          </div>
          <div className="mm-cta-icon">+</div>
        </motion.button>

        {/* Text Session - Secondary */}
        <motion.button
          className="mm-cta-text"
          onClick={onStartTextSession}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
        >
          Start Text Session
        </motion.button>
      </div>

      {/* Recent Sessions */}
      {recentSessions.length > 0 && (
        <div className="mm-recent-section">
          <div className="mm-section-header">
            <h3 className="mm-section-title">Recent Sessions</h3>
          </div>

          <div className="mm-sessions-grid">
            {recentSessions.map((session, index) => (
              <motion.div
                key={session.id}
                className="mm-session-item"
                onClick={() => onResumeSession(session.id)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="mm-session-header">
                  <div className="mm-session-title-group">
                    <h4 className="mm-session-name">{session.title}</h4>
                  </div>
                  {session.frameworkName && (
                    <span className="mm-session-badge">{session.frameworkName}</span>
                  )}
                </div>
                <div className="mm-session-footer">
                  <span className="mm-session-meta">{formatDate(session.updatedAt || session.createdAt)}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Framework Teaser */}
      <div className="mm-framework-teaser">
        <h3 className="mm-framework-teaser-title">Structured Coaching</h3>
        <p className="mm-framework-teaser-description">
          Use proven frameworks like GROW, Solution-Focused, and CLEAR to guide your sessions
        </p>
        <a
          className="mm-framework-link"
          onClick={(e) => {
            e.preventDefault();
            onStartWithFramework && onStartWithFramework();
          }}
        >
          Explore Frameworks
          <span className="mm-framework-arrow">→</span>
        </a>
      </div>
    </div>
  );
};

export default HomeViewPremium;
